import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import PlacesAutocompleteInput from '../../../../components/ui/places-autocomplete-input';
import { isInsidePacContainer } from '../../../../lib/googleMapsLoader';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import { resolveAddressDisplay } from './resolveAddressDisplay';

/**
 * VerifyAddressDialog
 *
 * Lets an operator pick a Google Place result and store it on
 * `companies/{cid}/leads/{id}.location` for use by the Property Location
 * map. Deliberately does NOT touch `lead.address` — the CRM/service
 * address stays operator-controlled and conceptually separate from the
 * verified Google location.
 *
 * Captured metadata (durable schema, used across the platform):
 *   - placeId, formattedAddress, lat, lng, addressComponents
 *   - verified (bool), verifiedAt, verifiedBy
 *   - source — provenance for the verification event itself. Currently
 *     always `'manual'` from this dialog (operator typed/picked in the
 *     CRM). Other surfaces (imports, web forms) will write their own
 *     source values when they wire location capture.
 */
const SOURCE = 'manual';

const componentsFromPlace = (place) => {
  const out = {};
  for (const c of place.address_components || []) {
    for (const t of c.types || []) {
      out[t] = c.short_name || c.long_name;
    }
  }
  return out;
};

const VerifyAddressDialog = ({ client, isOpen, onClose, onVerified }) => {
  const { companyId } = useCompany();

  const normalizedAddress = React.useMemo(
    () => resolveAddressDisplay(client),
    [client]
  );

  const [draftAddress, setDraftAddress] = useState('');
  const [picked, setPicked] = useState(null); // raw Place result
  const [saving, setSaving] = useState(false);
  const initialAddress = useRef(normalizedAddress);

  React.useEffect(() => {
    if (isOpen) {
      // Reset the autocomplete input on open. Pre-filling with the service
      // address biases Google suggestions geographically (iter-271 finding).
      // The full service address is still visible in the snippet above the
      // input, so the operator has plenty of context.
      setDraftAddress('');
      initialAddress.current = normalizedAddress;
      setPicked(null);
    }
  }, [isOpen, normalizedAddress]);

  const handlePlace = (place) => {
    if (!place?.formatted_address || !place?.geometry?.location) return;
    setPicked(place);
    setDraftAddress(place.formatted_address);
  };

  const handleSave = async () => {
    if (!picked || !client?.id || !companyId) {
      toast.error('Pick an address from the Google suggestions to verify.');
      return;
    }
    setSaving(true);
    try {
      const lat = typeof picked.geometry.location.lat === 'function'
        ? picked.geometry.location.lat()
        : picked.geometry.location.lat;
      const lng = typeof picked.geometry.location.lng === 'function'
        ? picked.geometry.location.lng()
        : picked.geometry.location.lng;

      const location = {
        verified: true,
        verifiedAt: serverTimestamp(),
        verifiedBy: auth?.currentUser?.uid || null,
        placeId: picked.place_id || null,
        formattedAddress: picked.formatted_address,
        lat,
        lng,
        addressComponents: componentsFromPlace(picked),
        source: SOURCE,
      };

      const ref = doc(db, 'companies', companyId, 'leads', client.id);
      // IMPORTANT: do NOT overwrite lead.address — the CRM/service address
      // stays operator-controlled. Verified Google data lives at lead.location.
      await updateDoc(ref, { location });

      toast.success('Address verified with Google');
      onVerified?.(location);
      onClose?.();
    } catch (err) {
      console.error('[VerifyAddressDialog] Save failed:', err);
      toast.error('Could not save verified address');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !saving && onClose?.()}>
      <DialogContent
        className="max-w-md"
        data-testid="verify-address-dialog"
        // Don't close when the operator clicks a Google suggestion — Google
        // appends `.pac-container` to <body>, which Radix sees as outside.
        onInteractOutside={(e) => {
          if (isInsidePacContainer(e)) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isInsidePacContainer(e)) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-emerald-600" />
            Verify Address with Google
          </DialogTitle>
          <DialogDescription>
            Pick the matching property from Google&apos;s suggestions to set the
            satellite view and store a verified location. The customer&apos;s
            service address (above) is not changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {normalizedAddress && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                Service address on file
              </p>
              <p className="mt-0.5 text-sm text-gray-800">{normalizedAddress}</p>
            </div>
          )}

          <div>
            <Label htmlFor="verify-address-input" className="text-xs">
              Search Google
            </Label>
            <PlacesAutocompleteInput
              id="verify-address-input"
              value={draftAddress}
              onChange={setDraftAddress}
              onPlaceSelected={handlePlace}
              placeholder="Start typing the property address…"
              className="mt-1"
              data-testid="verify-address-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Tip: include the city or ZIP for more accurate suggestions.
            </p>
          </div>

          {picked && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
              data-testid="verify-address-picked"
            >
              <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">
                Google match
              </p>
              <p className="mt-0.5 text-sm font-semibold">{picked.formatted_address}</p>
              <p className="mt-0.5 text-[10px] text-emerald-700">
                Place ID: {picked.place_id?.slice(0, 16)}…
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="verify-address-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!picked || saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="verify-address-save-btn"
          >
            {saving ? 'Saving…' : 'Verify'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerifyAddressDialog;
